import {DownOutlined, ExclamationCircleOutlined} from '@ant-design/icons/lib';
import {Button, Col, Dropdown, Menu, message, Modal, Row, Space, Tabs} from 'antd';
import {CardProps} from 'antd/es/card';
import {FormProps} from 'antd/es/form';
import {
  AccountFieldsFragment,
  AccountStatus,
  Currencies,
  LegalForm,
  PibisiStatus,
  ProvidersDocument,
  UpdateAccountInput,
  useAccountQuery,
  useAccountStatsQuery,
  useCreatePibisiUserMutation,
  useDeleteAccountMutation,
  useDisconnectMasterAccountMutation,
  useImpersonateMutation,
  useOnUpdateAccountSubscription,
  useRegisterMoneiXMerchantMutation,
  useRestoreAccountMutation,
  useUpdateAccountMutation
} from 'api/generated';
import {PageHeader, Spacer} from 'components';
import {AccountTags} from 'components/AccountTags';
import {WebhooksSettings} from 'components/WebhookSettings';
import {useAccessLevel} from 'hooks/useAccessLevel';
import {useTestModeState} from 'hooks/useTestModeState';
import {UserContext} from 'lib/userContext';
import {nullify} from 'lib/utils';
import React, {useContext, useEffect, useState} from 'react';
import {RouteComponentProps, useHistory, useLocation} from 'react-router-dom';
import {AccountSettings} from 'routes/Account/AccountSettings';
import {AccountTimeline} from 'routes/Account/AccountTimeline';
import {AdditionalDataModal} from 'routes/Account/AdditionalDataModal';
import {BillingHistory} from 'routes/Account/Billing/BillingHistory';
import {ContactPerson} from 'routes/Account/ContactPerson';
import {LegalDetails} from 'routes/Account/LegalDetails';
import {PreviewInvoice} from 'routes/Account/PreviewInvoice';
import {RejectAccount} from 'routes/Account/RejectAccount';
import {RiskAssessment} from 'routes/Account/RiskAssessment';
import {StatsSummary} from 'routes/Account/StatsSummary';
import {ReportsOverview} from 'routes/Dashboard/ReportsOverview';
import {TodayChart} from 'routes/Dashboard/TodayChart';
import {Payments} from 'routes/Payments';
import {config} from 'stage.config';
import {StringParam, useQueryParam} from 'use-query-params';
import {accountQuery} from '../../api/queries';
import {checkFromAndorra, checkSimplifiedAccount} from '../../lib/accountUtils';
import {AccountStatsTable} from '../Dashboard/AccountStatsTable';
import {NotFound} from '../NotFound';
import {BrandingSettings} from './AccountBranding';
import {AccountDetails} from './AccountDetails';
import {AccountTitle} from './AccountTitle';
import {ApproveAccount} from './ApproveAccount';
import {AuthorizedUsers} from './AuthorizedUsers/AuthorizedUsers';
import {BillingDetails} from './Billing/BillingDetails';
import {BusinessDetails} from './BusinessDetails';
import {CalculateRiskAssessmentModal} from './CalculateRiskAssessmentModal';
import {ChurnedReasonForm} from './ChurnedReasonForm';
import {Documents} from './Documents/Documents';
import {MasterAccountsListModal} from './MasterAccountsListModal';
import {AccountBalancesDetails} from './Payout/AccountBalancesDetails';
import {PayoutDetails} from './Payout/PayoutDetails';
import {PayoutSettings} from './Payout/PayoutSettings';
import {Pibisi} from './Pibisi/Pibisi';
import {Providers} from './Providers';
import {PublicBusinessDetails} from './PublicBusinessDetails';
import {RegisterPproModal} from './RegisterPproModal';
import {RepresentativeDetails} from './RepresentativeDetails';
import {SalesDetails} from './SalesDetails';
import {StatusTags} from './StatusTags';
import {Stores} from './Stores/Stores';
import {Subscriptions} from './Subscriptions';
import {SuspendAccountModal} from './SuspendAccountModal';
import {TokenizationMethods} from './TokenizationMethods';
import {WebsiteReport} from './WebsiteReport';

const {TabPane} = Tabs;

const shouldShowPibisiTab = (account?: AccountFieldsFragment | null) => {
  if (!account) return false;
  const {representatives, business} = account;
  const repMatch = representatives?.some(
    (rep) => rep.pibisi?.status && rep.pibisi.status !== PibisiStatus.VERIFIED
  );
  if (repMatch) return true;
  if (business?.legalForm === LegalForm.COMPANY) {
    return business.pibisi?.status !== PibisiStatus.VERIFIED;
  }
  return false;
};

export const Account: React.FC<RouteComponentProps<{accountId: string}>> = ({match}) => {
  const {accountId} = match.params;
  const location: any = useLocation();
  const [isTestMode] = useTestModeState();
  const history = useHistory();
  const [impersonate] = useImpersonateMutation();
  const [_activeTab, setActiveTab] = useQueryParam('tab', StringParam);
  const [deleteAccount] = useDeleteAccountMutation();
  const [restoreAccount] = useRestoreAccountMutation();
  const [updateAccount] = useUpdateAccountMutation({
    updateQuery: {query: accountQuery, variables: {id: accountId}}
  });
  const [disconnectMasterAccount] = useDisconnectMasterAccountMutation();
  const [registerAccount] = useRegisterMoneiXMerchantMutation();
  const [createPibisi] = useCreatePibisiUserMutation();
  const [churnReasonFormOpen, setChurnReasonFormOpen] = useState(false);
  const [masterAccountsModalOpen, setMasterAccountsModalOpen] = useState(false);
  const [isSuspendAccountModalOpen, setIsSuspendAccountModalOpen] = useState(false);
  const [isAdditionalDataModalOpen, setIsAdditionalDataModalOpen] = useState(false);
  const [isCalculateRiskAssessmentModalOpen, setIsCalculateRiskAssessmentModalOpen] =
    useState(false);
  const [isPproModalOpen, setIsPproModalOpen] = useState(false);
  const fromMasterAccount = location.state?.fromMasterAccount;

  const {data: accountStatsData, loading: loadingStats} = useAccountStatsQuery({
    variables: {accountId}
  });

  const stats = accountStatsData?.accountStats.stats;

  const {userEmail} = useContext(UserContext);
  const {isAdmin, isMaster} = useAccessLevel();

  const activeTab = _activeTab || 'details';

  const {data, loading, refetch} = useAccountQuery({
    variables: {id: accountId},
    context: {useApolloNetworkStatus: false}
  });

  const account = data?.account;
  const {test, testAccountId, prodAccountId} = account || {};

  const [currency, setCurrency] = useState<Currencies>(
    (account?.defaultCurrency as Currencies) || Currencies.EUR
  );

  useOnUpdateAccountSubscription({
    variables: {accountId},
    onSubscriptionData: () => {
      setTimeout(refetch, 1000);
    }
  });

  useEffect(() => {
    setTimeout(refetch, 1000);
  }, []);

  useEffect(() => {
    if (isTestMode && !test && testAccountId) {
      setTimeout(() => {
        history.push(`/accounts/${testAccountId}`);
      }, 250);
    }
    if (!isTestMode && test && prodAccountId) {
      setTimeout(() => {
        history.push(`/accounts/${prodAccountId}`);
      }, 250);
    }
  }, [isTestMode, history, test, testAccountId, prodAccountId]);

  if (!account && !loading) {
    return (
      <NotFound
        message={
          <>
            Sorry, account with id <strong>{accountId}</strong> does not exist
          </>
        }
      />
    );
  }

  const isSimplified = checkSimplifiedAccount(account);
  const isFromAndorra = checkFromAndorra(account);
  const isConfigured = ![AccountStatus.NOT_CONFIGURED, AccountStatus.PENDING_REVIEW].includes(
    account?.status!
  );
  const isNotPending = ![AccountStatus.PENDING_CONTRACT, AccountStatus.PENDING_APPROVAL].includes(
    account?.status!
  );

  const canViewPayments = [
    AccountStatus.ACTIVE,
    AccountStatus.APPROVED,
    AccountStatus.PENDING_REVIEW,
    AccountStatus.NOT_CONFIGURED,
    AccountStatus.SUSPENDED,
    AccountStatus.REJECTED
  ].includes(account?.status!);

  const showPibisiTab = shouldShowPibisiTab(account);

  const cardProps: CardProps = {
    size: 'small',
    loading,
    style: {
      marginBottom: 25
    }
  };

  const narrowFormProps: FormProps = {
    labelAlign: 'left',
    labelCol: {span: 8},
    wrapperCol: {span: 16},
    colon: false
  };

  const formProps: FormProps = {
    labelAlign: 'left',
    labelCol: {span: 6},
    wrapperCol: {span: 8},
    colon: false
  };

  const impersonateUser = async () => {
    if (!account?.adminEmail) return;
    await impersonate({
      variables: {
        input: {
          adminEmail: userEmail,
          userEmail: account.adminEmail,
          accountId: account.id
        }
      }
    });
    const win = window.open(`${config.dashboardUrl}?action=signIn&email=${userEmail}`, '_blank');
    win?.focus();
  };

  const handleDeleteAccount = async () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content:
        'This action will delete account and disable the user (you can restore it if needed). The user will not be able to login anymore.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteAccount({variables: {id: accountId}})
    });
  };

  const handleSuspendAccount = () => setIsSuspendAccountModalOpen(true);

  const handleAdditionalData = () => setIsAdditionalDataModalOpen(true);

  const handleRegisterPpro = () => setIsPproModalOpen(true);

  const handleCalculateRiskScore = () => setIsCalculateRiskAssessmentModalOpen(true);

  const handleRegisterAccount = async () => {
    Modal.confirm({
      title: 'Are you sure?',
      content:
        'This action will register new merchant account in MONEI X (Finaro). Please make sure that you have correct business details.',
      okText: 'Confirm',
      onOk: () =>
        registerAccount({
          variables: {id: accountId},
          refetchQueries: [{query: ProvidersDocument, variables: {accountId}}]
        })
    });
  };
  const handleRestoreAccount = async () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'This action will restore the account.',
      okText: 'Confirm',
      onOk: () => restoreAccount({variables: {id: accountId}})
    });
  };

  const handleGeneratePibisi = async () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: (
        <>
          This will generate PIBISI report (Prevention of Money Laundering and Financing of
          Terrorism) for the company and each representative.
        </>
      ),
      okText: 'Generate report',
      onOk: async () => {
        await createPibisi({
          variables: {
            input: {
              accountId: accountId,
              legalForm: account?.business?.legalForm!
            }
          }
        });
        message.success('The request usually takes less than 5 minutes to process.');
      }
    });
  };

  const handleDisconnectMasterAccount = async () => {
    Modal.confirm({
      icon: <ExclamationCircleOutlined />,
      title: 'Are you sure?',
      content: 'This will disconnect the master account from the account.',
      okText: 'Confirm',
      onOk: async () => {
        await disconnectMasterAccount({variables: {accountId: accountId}});
        message.success('Master account disconnected.');
      }
    });
  };

  const handleUpdateAccount = (input: UpdateAccountInput) => {
    return updateAccount({variables: {input: nullify(input)}});
  };

  const menuItems = [];

  if (isAdmin) {
    if (
      [
        AccountStatus.PENDING_APPROVAL,
        AccountStatus.NOT_CONFIGURED,
        AccountStatus.APPROVED,
        AccountStatus.ACTIVE
      ].includes(account?.status!)
    ) {
      menuItems.push(
        <Menu.Item key={1} onClick={handleRegisterAccount}>
          Register in MONEI X
        </Menu.Item>
      );
    }

    if ([AccountStatus.APPROVED, AccountStatus.ACTIVE].includes(account?.status!)) {
      menuItems.push(
        <Menu.Item key={2} onClick={handleRegisterPpro}>
          Register in PPRO
        </Menu.Item>
      );
    }

    if (!isSimplified) {
      menuItems.push(
        <Menu.Item key={3} onClick={handleGeneratePibisi}>
          Generate PIBISI report
        </Menu.Item>
      );
    }

    if (
      ![AccountStatus.NOT_CONFIGURED, AccountStatus.DELETED, AccountStatus.SUSPENDED].includes(
        account?.status!
      )
    )
      menuItems.push(
        <Menu.Item key={4} onClick={handleAdditionalData}>
          Request additional data
        </Menu.Item>
      );

    if (!isSimplified) {
      menuItems.push(
        <Menu.Item key={5} onClick={handleCalculateRiskScore}>
          Calculate risk score
        </Menu.Item>
      );
    }

    menuItems.push(
      <Menu.Item key={6} onClick={impersonateUser}>
        Impersonate
      </Menu.Item>
    );

    menuItems.push(
      <Menu.Item
        key={7}
        onClick={() => {
          if (account?.masterAccountId) {
            handleDisconnectMasterAccount();
          } else {
            setMasterAccountsModalOpen(true);
          }
        }}>
        {account?.masterAccountId ? 'Disconnect master account' : 'Connect master account'}
      </Menu.Item>
    );

    if (account?.status !== AccountStatus.SUSPENDED) {
      menuItems.push(
        <Menu.Item key={8} onClick={handleSuspendAccount}>
          Suspend
        </Menu.Item>
      );
    }

    if (account?.status !== AccountStatus.DELETED) {
      menuItems.push(
        <Menu.Item key={9} onClick={handleDeleteAccount}>
          Delete
        </Menu.Item>
      );
    }

    if ([AccountStatus.DELETED, AccountStatus.SUSPENDED].includes(account?.status!)) {
      menuItems.push(
        <Menu.Item key={10} onClick={handleRestoreAccount}>
          Restore
        </Menu.Item>
      );
    }
  }

  if (isMaster) {
    menuItems.push(
      <Menu.Item key={11}>
        <PreviewInvoice accountId={accountId} iban={account?.iban} />
      </Menu.Item>
    );
  }

  if (isAdmin && account?.status === AccountStatus.ACTIVE) {
    menuItems.push(
      <Menu.Item key={12} onClick={() => setChurnReasonFormOpen(true)}>
        Update churn reason
        <ChurnedReasonForm
          account={account}
          isOpen={churnReasonFormOpen}
          onClose={() => {
            setTimeout(() => {
              setChurnReasonFormOpen(false);
            });
          }}
        />
      </Menu.Item>
    );
  }

  type TabProps = {
    tab: string;
    key: string;
    children: JSX.Element;
    enabled: boolean;
  };
  const tabs: TabProps[] = [
    {
      tab: 'Details',
      key: 'details',
      children: (
        <Row gutter={20}>
          <Col span={isAdmin ? 16 : 24}>
            <AccountDetails
              accountId={accountId}
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <ContactPerson
              accountId={accountId}
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            {isAdmin && (
              <SalesDetails
                accountId={accountId}
                account={account}
                onSubmit={handleUpdateAccount}
                cardProps={cardProps}
                formProps={narrowFormProps}
              />
            )}
            {isAdmin && !isSimplified && (
              <RiskAssessment
                account={account}
                onSubmit={handleUpdateAccount}
                cardProps={cardProps}
                formProps={narrowFormProps}
              />
            )}
            {isAdmin && !isSimplified && <WebsiteReport account={account} cardProps={cardProps} />}
            <BusinessDetails
              accountId={accountId}
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <PublicBusinessDetails
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <LegalDetails
              accountId={accountId}
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
            <RepresentativeDetails
              accountId={accountId}
              account={account}
              onSubmit={handleUpdateAccount}
              cardProps={cardProps}
              formProps={narrowFormProps}
            />
          </Col>
          {isAdmin && (
            <Col span={8}>
              <AccountTimeline key={accountId} />
            </Col>
          )}
        </Row>
      ),
      enabled: true
    },
    {
      tab: 'Documents',
      key: 'documents',
      children: <Documents cardProps={cardProps} accountId={accountId} />,
      enabled: isMaster
    },
    {
      tab: 'Settings',
      key: 'settings',
      children: (
        <AccountSettings
          accountId={accountId}
          account={account}
          onSubmit={handleUpdateAccount}
          cardProps={cardProps}
          formProps={formProps}
        />
      ),
      enabled: isAdmin && isMaster
    },
    {
      tab: 'Billing',
      key: 'billing',
      children: (
        <>
          <BillingHistory
            accountId={accountId}
            account={account}
            onSubmit={handleUpdateAccount}
            cardProps={cardProps}
            formProps={formProps}
          />
          <BillingDetails
            accountId={accountId}
            account={account}
            onSubmit={handleUpdateAccount}
            cardProps={cardProps}
            formProps={formProps}
          />
        </>
      ),
      enabled: !isFromAndorra && isMaster
    },
    {
      tab: 'Payouts',
      key: 'payouts',
      children: (
        <>
          <AccountBalancesDetails
            accountId={accountId}
            account={account}
            cardProps={cardProps}
            onSubmit={handleUpdateAccount}
            formProps={formProps}
          />
          <PayoutSettings
            accountId={accountId}
            account={account}
            onSubmit={handleUpdateAccount}
            cardProps={cardProps}
            formProps={formProps}
          />
          <PayoutDetails
            accountId={accountId}
            account={account}
            onSubmit={handleUpdateAccount}
            cardProps={cardProps}
            formProps={formProps}
          />
        </>
      ),
      enabled: !isSimplified && isMaster
    },
    {
      tab: 'Stores',
      key: 'stores',
      children: <Stores accountId={accountId} cardProps={cardProps} />,
      enabled: isAdmin
    },
    {
      tab: 'Payment methods',
      key: 'payment-methods',
      children: (
        <>
          <Providers accountId={accountId} cardProps={cardProps} />
          <TokenizationMethods
            accountId={accountId}
            account={account}
            onSubmit={handleUpdateAccount}
            cardProps={cardProps}
            formProps={formProps}
          />
        </>
      ),
      enabled: isAdmin && isNotPending
    },
    {
      tab: 'Analytics',
      key: 'analytics',
      children: (
        <>
          <AccountStatsTable stats={stats} loading={loadingStats} />
          <TodayChart accountId={accountId} selectedCurrency={currency} setCurrency={setCurrency} />
          <ReportsOverview accountId={accountId} selectedCurrency={currency} />
        </>
      ),
      enabled: canViewPayments
    },
    {
      tab: 'Payments',
      key: 'payments',
      children: <Payments accountId={accountId} />,
      enabled: isMaster && canViewPayments
    },
    {
      tab: 'Subscriptions',
      key: 'subscriptions',
      children: <Subscriptions accountId={accountId} />,
      enabled: isMaster && canViewPayments
    },
    {
      tab: 'Users',
      key: 'users',
      children: <AuthorizedUsers cardProps={cardProps} accountId={accountId} />,
      enabled: isMaster
    },
    {
      tab: 'Webhooks',
      key: 'webhooks',
      children: <WebhooksSettings accountId={accountId} cardProps={cardProps} />,
      enabled: isAdmin && isMaster && canViewPayments
    },
    {
      tab: 'Branding',
      key: 'branding',
      children: <BrandingSettings />,
      enabled: isMaster && isNotPending && isConfigured
    },
    {
      tab: 'PIBISI',
      key: 'pibisi',
      children: <Pibisi account={account} cardProps={cardProps} />,
      enabled: isAdmin && showPibisiTab
    }
  ];

  return (
    <>
      <PageHeader loading={loading} backPath={fromMasterAccount ? '/master-accounts' : '/accounts'}>
        <AccountTitle account={account} />
        <StatusTags account={account} />
        <AccountTags account={account} />
        <Spacer />
        <StatsSummary stats={stats} />
      </PageHeader>
      <SuspendAccountModal
        account={account}
        isOpen={isSuspendAccountModalOpen}
        setIsOpen={setIsSuspendAccountModalOpen}
      />
      <AdditionalDataModal
        account={account}
        isOpen={isAdditionalDataModalOpen}
        setIsOpen={setIsAdditionalDataModalOpen}
      />
      <MasterAccountsListModal
        account={account}
        isOpen={masterAccountsModalOpen}
        setIsOpen={setMasterAccountsModalOpen}
      />
      <CalculateRiskAssessmentModal
        account={account}
        isOpen={isCalculateRiskAssessmentModalOpen}
        setIsOpen={setIsCalculateRiskAssessmentModalOpen}
      />
      <RegisterPproModal
        accountId={accountId}
        isOpen={isPproModalOpen}
        setIsOpen={setIsPproModalOpen}
      />
      <Tabs
        activeKey={activeTab}
        onChange={(tab) => setActiveTab(tab)}
        type="card"
        tabBarExtraContent={
          <Space>
            {menuItems.length > 0 && (
              <Dropdown overlay={<Menu>{menuItems}</Menu>} placement="bottomRight">
                <Button>
                  Actions <DownOutlined />
                </Button>
              </Dropdown>
            )}
            {isAdmin &&
              !account?.riskProcessing &&
              [
                AccountStatus.NOT_CONFIGURED,
                AccountStatus.PENDING_APPROVAL,
                AccountStatus.PENDING_REVIEW
              ].includes(account?.status!) && (
                <>
                  <RejectAccount account={account} />
                  <ApproveAccount accountId={accountId} />
                </>
              )}
          </Space>
        }>
        {tabs.map(
          (tab) =>
            tab.enabled && (
              <TabPane tab={tab.tab} key={tab.key}>
                {tab.children}
              </TabPane>
            )
        )}
      </Tabs>
    </>
  );
};
